import {
    AfterContentInit,
    AfterViewChecked,
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    ContentChild,
    ElementRef,
    EventEmitter,
    HostListener,
    inject,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { FormControlDirective, UntypedFormControl } from '@angular/forms';

import { BehaviorSubject, Observable, of } from 'rxjs';
import {
    debounceTime,
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    share,
    startWith,
    switchMap,
    take,
    takeUntil,
    tap,
    withLatestFrom
} from 'rxjs/operators';

import { AsyncPipe, NgFor, NgIf, NgSwitch, NgSwitchCase, NgSwitchDefault, NgTemplateOutlet } from '@angular/common';
import { isEqual } from 'lodash-es';
import { BaseContainer } from '../../../../core/common/containers/base.abstract.container';
import { ShDroppanelComponent } from '../../../../elements/components/droppanel/droppanel.component';
import { ShClickOutsideDirective } from '../../../../elements/directives/click-outside/click-outside.directive';
import { ClearMarginModifiers } from '../../../../elements/entities/clear-margin-modifier.entity';
import { ArrowNavigationHelper } from '../../../../elements/helpers/arrow-navigation.helper';
import { multiFormSelectOptionWrapper } from '../../../../form-controls/helpers/multi-form-select-options-wrapper-helper';
import { AutocompleteFormSelectOption, FormSelectOption } from '../../../entities/form-select-option.entity';
import { MultiFormSelectOptions } from '../../../entities/multi-form-select-options.entity';
import { ShChildIndexDirective } from '../../../../elements/directives/child-index/child-index';

@Component({
    selector: 'sh-autocomplete',
    templateUrl: './autocomplete.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        NgIf,
        NgTemplateOutlet,
        ShDroppanelComponent,
        ShClickOutsideDirective,
        NgFor,
        NgSwitch,
        NgSwitchCase,
        NgSwitchDefault,
        AsyncPipe,
        ShChildIndexDirective
    ]
})
export class ShAutocompleteComponent<T>
    extends BaseContainer
    implements OnInit, AfterContentInit, AfterViewChecked, OnDestroy
{
    private readonly OPTION_QUERY: string = '.sh-droppanel__list-action:not(.-sh-disabled)';

    private readonly typing$: BehaviorSubject<string> = new BehaviorSubject<string>('');
    private readonly selectedValues$: BehaviorSubject<T[]> = new BehaviorSubject<T[]>([]);

    private readonly emitOnStart$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(true);

    public readonly isOpen$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
    public suggestions$!: Observable<MultiFormSelectOptions<T>>;
    public hasOptions$!: Observable<boolean>;
    public suggestionsSelected$!: Observable<boolean>;

    public formControl!: UntypedFormControl;

    private arrowNavigation: ArrowNavigationHelper;
    private hoverListener: () => void;

    @ContentChild(FormControlDirective) formControlRef?: FormControlDirective;
    @ContentChild(FormControlDirective, { read: ElementRef }) contentChild?: ElementRef<HTMLElement>;

    @Input() autocompleteFct!: (term: string) => Observable<MultiFormSelectOptions<T>>;
    @Input() debounce: number = 300;
    @Input() lockOnInvalid: boolean = false;
    @Input() inOverflownContainer = false;
    @Input() clearMarginModifiers?: ClearMarginModifiers;
    @Input() multiple = false;
    @Input() set selectedValues(selectedValues: T[]) {
        this.selectedValues$.next(selectedValues);
    }

    @Input() loadingMessage: string;
    @Input() noResultsMessage: string;

    @Input() minTextLength = 0;
    @Input() invalid: boolean; // Only needed if lockOnInvalid is true

    @Output() suggestionsSelected: EventEmitter<FormSelectOption<T>[]> = new EventEmitter<FormSelectOption<T>[]>();
    @Output() initialAutocompleteOptions: EventEmitter<MultiFormSelectOptions<T>> = new EventEmitter<
        MultiFormSelectOptions<T>
    >();

    @ViewChild('droppanel') droppanel?: ElementRef<HTMLElement>;
    @ViewChild('optionContainer') optionContainerElement?: ElementRef<HTMLElement>;

    @HostListener('keyup', ['$event']) onKeyUp(event: Event) {
        this.typing$.next(this.formControl.value);
    }

    @HostListener('keydown', ['$event']) onKeyDown(event: KeyboardEvent) {
        this.arrowNavigation.handleKeyDown({
            event,
            optionContainerElement: this.optionContainerElement,
            toggleContainer: this.contentChild,
            searchElement: this.contentChild,
            closeDropdown: () => this.closeDropdown()
        });
    }

    // Injectors
    private readonly cdr: ChangeDetectorRef = inject(ChangeDetectorRef);

    constructor() {
        super();
    }

    ngOnInit(): void {
        this.initArrowNavigation();
        this.initOpenListener();
    }

    ngAfterContentInit(): void {
        if (this.formControlRef) {
            this.formControl = this.formControlRef.control;
            this.initAutocompleteSuggestions();
            this.initHasOptionsStream();

            this.hoverListener = this.onHoverSearch.bind(this);
            this.contentChild?.nativeElement.addEventListener('mouseenter', this.hoverListener);
        }
    }

    ngAfterViewChecked() {
        this.arrowNavigation.manageFocus(this.isOpen$.value, this.optionContainerElement, this.contentChild);
    }

    private initArrowNavigation(): void {
        this.arrowNavigation = new ArrowNavigationHelper(
            this.cdr,
            this.isOpen$,
            this.destroy$,
            this.OPTION_QUERY,
            true
        );
    }

    private initAutocompleteSuggestions(): void {
        this.typing$.pipe(takeUntil(this.destroy$)).subscribe(term => {
            if (!term || term.length === 0) {
                this.closeDropdown();
            }
        });

        this.suggestions$ = this.typing$.pipe(
            tap(term => {
                if (this.lockOnInvalid && this.invalid) {
                    this.closeDropdown();
                } else if (this.loadingMessage && !!term?.trim()) {
                    this.openDropdown();
                }
            }),
            tap(() => this.setLoading(true)),
            debounceTime(this.debounce),
            startWith(''),
            pairwise(),
            tap(([prev, curr]) => {
                // Stop loading if distinctUntilChanged will block the stream
                if (prev === curr) {
                    this.setLoading(false);
                }
            }),
            map(([prev, curr]) => curr),
            distinctUntilChanged(),
            filter(() => !this.lockOnInvalid || !this.invalid),
            switchMap(term =>
                term !== null && term.length >= this.minTextLength
                    ? this.autocompleteFct(term)
                    : of(multiFormSelectOptionWrapper<T>([]))
            ),
            tap((autocompleteValues: MultiFormSelectOptions<T>) => {
                if (this.emitOnStart$.value) {
                    this.initialAutocompleteOptions.emit(autocompleteValues);
                }
                this.emitOnStart$.next(false);
            }),
            withLatestFrom(this.typing$),
            tap(([values, term]) => {
                if (!term || term.length === 0) {
                    this.closeDropdown();
                    return;
                }

                if (values.options.length === 0 && !this.noResultsMessage) {
                    this.closeDropdown();
                    return;
                }

                if (!this.loadingMessage) {
                    // Dialog should already be open, if it isn't, it was closed manually
                    this.openDropdown();
                }
            }),
            map(([values, term]) => values),
            withLatestFrom(this.selectedValues$),
            map(([suggestions, selectedValues]) => this.setValuesChecked(suggestions, selectedValues)),
            tap(() => this.setLoading(false)),
            share()
        ) as Observable<MultiFormSelectOptions<T>>;
    }

    private setValuesChecked(suggestions: MultiFormSelectOptions<T>, selectedValues: T[]): MultiFormSelectOptions<T> {
        const options: AutocompleteFormSelectOption<T>[] = suggestions.options.map(sugg => ({
            label: sugg.label,
            checked: !!selectedValues.find(selectedValue => isEqual(sugg.value, selectedValue)),
            value: sugg.value,
            // only add properties if they exist
            ...(!!sugg.disabled && { disabled: sugg.disabled }),
            ...(!!sugg.disabledInfoMsg && { disabledInfoMsg: sugg.disabledInfoMsg })
        }));
        return {
            ...suggestions,
            options: options
        };
    }

    private initHasOptionsStream(): void {
        this.hasOptions$ = this.suggestions$.pipe(
            take(1),
            map(() => true)
        );
    }

    private initOpenListener(): void {
        this.isOpen$.pipe().subscribe(isOpen => {
            if (isOpen) {
                this.arrowNavigation.resetFocus();
                this.activate();
            } else if (this.isActiveComponent()) {
                this.deactivate();
            }
        });
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
        this.isOpen$.complete();
        this.typing$.next('');
        this.typing$.complete();

        this.contentChild?.nativeElement.removeEventListener('mouseenter', this.hoverListener);
    }

    public openDropdown(): void {
        this.isOpen$.next(true);
    }

    public closeDropdown(): void {
        this.isOpen$.next(false);
    }

    onSelect(option: AutocompleteFormSelectOption<T>) {
        this.formControl.setValue(option.value);
        // remove 'checked' property from result
        const { checked, ...resultOption } = option;
        this.suggestionsSelected.emit([resultOption]);
        this.closeDropdown();
    }

    public get focusIndex(): number {
        return this.arrowNavigation?.focusIndex ?? -1;
    }

    public onHover(event: MouseEvent): void {
        this.arrowNavigation.onHover(event.target as HTMLElement, this.optionContainerElement);
    }

    public onHoverSearch(): void {
        this.arrowNavigation.setSearchFocusIndex(-1);
    }
}
